import { useTranslation } from 'react-i18next'
import { AlertTriangle, ArrowLeft, FileQuestion, RotateCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { UnavailableKind } from './itemUnavailableKind'


/** The item nouns under `common:unavailable.items` (each with its own gendered title per language). */
export type UnavailableItem =
  | 'event' | 'course' | 'lesson' | 'content' | 'product' | 'campaign' | 'certificateTemplate' | 'certificate'
  | 'form' | 'survey' | 'call' | 'proposal' | 'ticket' | 'round' | 'classified' | 'download' | 'bookingResource'
  | 'space' | 'member' | 'community' | 'communityRequest' | 'reviewCycle' | 'developmentPlan' | 'oneOnOne'
  | 'objective' | 'goalCycle' | 'upvoteTopic' | 'workflow' | 'run' | 'parcel' | 'company' | 'sale' | 'subscription'

interface ItemUnavailableProps {
  kind: UnavailableKind
  item: UnavailableItem
  /** Leaves the item: the list it came from. Without it the "missing" state shows no button. */
  onBack?: () => void
  /** Overrides the "See the <items>" label of the back button. */
  backLabel?: string
  /** Reloads the item; the "error" state shows "Try again" only when given. */
  onRetry?: () => void
  /** Wraps the card in the page's padding; off when the caller already sits inside a padded container. */
  padded?: boolean
  className?: string
  'data-testid'?: string
}

/**
 * One screen for an item that cannot be shown: "this <item> no longer exists" (gray, back to the list)
 * when the server says it is not there, or "we couldn't open the <item> right now" (amber, try again)
 * when the request failed. A deleted link should never look like a crash.
 */
export function ItemUnavailable({ kind, item, onBack, backLabel, onRetry, padded = true, className, 'data-testid': testId }: ItemUnavailableProps) {
  const { t } = useTranslation('common')
  const missing = kind === 'missing'
  const Icon = missing ? FileQuestion : AlertTriangle
  const back = backLabel || t(`unavailable.items.${item}.back`)

  return (
    <div className={cn(padded && 'px-4 py-10 sm:px-6', className)}>
      <div
        role="alert"
        className="mx-auto max-w-md rounded-xl bg-white p-8 text-center shadow-sm"
        data-testid={testId || (missing ? 'item-missing' : 'item-load-error')}
        data-item={item}
      >
        <div className={cn('mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full', missing ? 'bg-gray-100' : 'bg-amber-50')}>
          <Icon className={cn('h-6 w-6', missing ? 'text-gray-500' : 'text-amber-500')} />
        </div>
        <h2 className="text-base font-semibold text-gray-900">{t(`unavailable.items.${item}.${missing ? 'missing' : 'error'}`)}</h2>
        <p className="mt-1.5 text-sm text-gray-600">{missing ? t(`unavailable.items.${item}.text`) : t('unavailable.errorText')}</p>
        {(missing ? onBack : onBack || onRetry) && (
          <div className="mt-6 flex flex-col-reverse justify-center gap-2 sm:flex-row">
            {missing ? (
              <Button variant="primary" onClick={onBack}><ArrowLeft className="mr-2 h-4 w-4" />{back}</Button>
            ) : (
              <>
                {onBack && <Button variant="outline" onClick={onBack}>{back}</Button>}
                {onRetry && <Button variant="primary" onClick={onRetry}><RotateCw className="mr-2 h-4 w-4" />{t('unavailable.retry')}</Button>}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
